export type Bitmap = createjs.Bitmap;
export type Container = createjs.Container;
export type DisplayObject = createjs.DisplayObject;
export type MouseEvent = createjs.MouseEvent;
export type MovieClip = createjs.MovieClip;
export type Rectangle = createjs.Rectangle;
export type Stage = createjs.Stage;
export type Text = createjs.Text;
export type Tween = createjs.Tween;

// ==================================================================================================
// private
// ==================================================================================================

// ------------------------------------------------------------------------------------------------
/**
 * Kludge function for addressing text rendering slight above where it's supposed to be.
 * This happens when a Flash TextField is exported from Adobe Animate to HTML5 canvas.
 * @see https://community.adobe.com/t5/animate-discussions/html5-canvas-publish-dynamic-text-position-changes/td-p/9193609
 * @see https://community.adobe.com/t5/animate-discussions/canvas-dynamic-text-sitting-too-high/m-p/10257300
 * @see https://community.adobe.com/t5/animate-discussions/html5-canvas-dynamic-text-styling-and-overlapping-dynamic-text-boxes-not-lining-up/td-p/9928121
 */
function drawTextKludge(context: CanvasRenderingContext2D, text: string, y: number): void {
  const textObject = <Text> this;

  // decrease number to move font up and increase to move down
  const ADJUSTMENT_SCALE = 0.2;

  // Adjust text position only if textBaseline is "top"
  if (textObject.textBaseline === 'top') {
    const lineHeight = textObject.lineHeight || textObject.getMeasuredLineHeight();
    y += lineHeight * ADJUSTMENT_SCALE;
  }

  // Chrome 17 will fail to draw the text if the last param is included but null,
  // so we feed it a large value instead:
  if (textObject.outline) {
    context.strokeText(text, 0, y, textObject.maxWidth || 0xFFFF);
  } else {
    context.fillText(text, 0, y, textObject.maxWidth || 0xFFFF);
  }
}

// ==================================================================================================
// public
// ==================================================================================================

// ------------------------------------------------------------------------------------------------
export function getMovieClipByName(container: Container, name: string): MovieClip {
  return (container ? <MovieClip> container.getChildByName(name) : null);
}

// ------------------------------------------------------------------------------------------------
export function getNominalBounds(display: DisplayObject): Rectangle {
  const NOMINAL_BOUNDS = 'nominalBounds';
  if (!(NOMINAL_BOUNDS in display)) {
    return (null);
  }

  const bounds = display[NOMINAL_BOUNDS];
  return (new createjs.Rectangle(bounds.x, bounds.y, bounds.width, bounds.height));
}

// ------------------------------------------------------------------------------------------------
export function getProperty(source: unknown, name: string, defaultValue?: unknown): unknown {
  if (!source || !name) {
    return (defaultValue);
  }

  const keys = name.split('.');
  const len = keys.length;
  let i = 0;
  let val = source;

  while (i < len) {
    const key = keys[i];

    if (val !== null && val !== undefined) {
      val = val[key];
      i += 1;
    } else {
      return (defaultValue);
    }
  }

  return (val !== null && val !== undefined ? val : defaultValue);
}

// ------------------------------------------------------------------------------------------------
export function getBooleanProperty(source: unknown, name: string,
  defaultValue?: boolean): boolean {
  return (<boolean> getProperty(source, name, defaultValue));
}

// ------------------------------------------------------------------------------------------------
export function getNumberProperty(source: unknown, name: string, defaultValue?: number): number {
  return (<number> getProperty(source, name, defaultValue));
}

// ------------------------------------------------------------------------------------------------
export function getStringProperty(source: unknown, name: string, defaultValue?: string): string {
  return (<string> getProperty(source, name, defaultValue));
}

// ------------------------------------------------------------------------------------------------
export function getTextByName(container: Container, name: string): Text {
  return (container ? <Text> container.getChildByName(name) : null);
}

// ------------------------------------------------------------------------------------------------
export function initTextKludge(): void {
  const DRAW_TEXT_LINE = '_drawTextLine';
  createjs.Text.prototype[DRAW_TEXT_LINE] = drawTextKludge;
}

// ------------------------------------------------------------------------------------------------
export function irandom(max: number): number {
  return (Math.floor(Math.random() * (max + 1)));
}

// ------------------------------------------------------------------------------------------------
export function irandomRange(min: number, max: number): number {
  return (Math.floor(Math.random() * (max - min + 1)) + min);
}

// ------------------------------------------------------------------------------------------------
export function randomRange(min: number, max: number): number {
  return (Math.random() * (max - min) + min);
}

// ------------------------------------------------------------------------------------------------
export function removeFromArray(array: unknown[], item: unknown): boolean {
  if (!array) {
    return (false);
  }

  const index = array.indexOf(item);
  if (index === -1) {
    return (false);
  }

  array.splice(index, 1);
  return (true);
}

// ------------------------------------------------------------------------------------------------
/**
 * Converts the first character of a string to upper-case
 *
 * @see
 * https://www.freecodecamp.org/news/three-ways-to-title-case-a-sentence-in-javascript-676a9175eb27/
 */
export function toProperCase(value: string): string {
  const _value = value || '';
  return (_value.charAt(0).toUpperCase() + _value.slice(1));
}
